import "@/App.css";
import { lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { LanguageProvider } from "./i18n/LanguageContext";
import { AuthProvider } from "./contexts/AuthContext";
import { MarketProvider } from "./contexts/MarketContext";
import { SubscriptionProvider } from "./contexts/SubscriptionContext";
import AnalyticsProvider from "./components/AnalyticsProvider";
import ScrollToTop from "./components/ScrollToTop";
import Layout from "./components/layout/Layout";
import HomePage from "./pages/HomePage"; // eager: LCP-critical, always first paint

// Lazy-loaded routes — each becomes its own JS chunk (route-level code splitting)
const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const LegalPages = lazy(() => import("./pages/LegalPages"));
const QuoteRequestPage = lazy(() => import("./pages/QuoteRequestPage"));
const ServicesPage = lazy(() => import("./pages/ServicesPage"));
const AdminDashboard = lazy(() => import("./pages/AdminDashboard"));
const AdminLeadsPage = lazy(() => import("./pages/AdminLeadsPage"));
const BlogPage = lazy(() => import("./pages/BlogPage"));
const BlogPostPage = lazy(() => import("./pages/BlogPostPage"));
const TeamPage = lazy(() => import("./pages/TeamPage"));
const AnalyseStrategiquePage = lazy(() => import("./pages/AnalyseStrategiquePage"));
const ResourcesPage = lazy(() => import("./pages/ResourcesPage"));
const ResourceArticlePage = lazy(() => import("./pages/ResourceArticlePage"));
const ContractorNetworkPage = lazy(() => import("./pages/ContractorNetworkPage"));
const DeveloperGuidePage = lazy(() => import("./pages/DeveloperGuidePage"));
const MyAnalysesPage = lazy(() => import("./pages/MyAnalysesPage"));
const PricingPage = lazy(() => import("./pages/PricingPage"));
const SharedLandPage = lazy(() => import("./pages/SharedLandPage"));
const SharedAnalysisPage = lazy(() => import("./pages/SharedAnalysisPage"));
const PaymentSuccessPage = lazy(() => import("./pages/PaymentSuccessPage"));
const InstitutionalSimulatorPage = lazy(() => import("./pages/InstitutionalSimulatorPage"));
const ChatWidget = lazy(() => import("./components/ChatWidget"));
const WhatsAppButton = lazy(() => import("./components/WhatsAppButton"));
// SEO Pages
const CityPage = lazy(() => import("./pages/CityPage"));
const LotSizePage = lazy(() => import("./pages/LotSizePage"));
const SEOArticlesPage = lazy(() => import("./pages/SEOArticlesPage"));
const SEOArticleDetailPage = lazy(() => import("./pages/SEOArticleDetailPage"));
const CitiesIndexPage = lazy(() => import("./pages/CitiesIndexPage"));
const LotSizesIndexPage = lazy(() => import("./pages/LotSizesIndexPage"));
const PrivacyPolicyPage = lazy(() => import("./pages/PrivacyPolicyPage"));
const TermsOfServicePage = lazy(() => import("./pages/TermsOfServicePage"));
// New PLEXTERRA pages
const TechnologiesPage = lazy(() => import("./pages/TechnologiesPage"));
const SectorsPage = lazy(() => import("./pages/SectorsPage"));
const ProjectsPage = lazy(() => import("./pages/ProjectsPage"));
const CareersPage = lazy(() => import("./pages/CareersPage"));
const SuccessStoriesPage = lazy(() => import("./pages/SuccessStoriesPage"));
const MLISelectCalculatorPage = lazy(() => import("./pages/MLISelectCalculatorPage"));
const LicensingPage = lazy(() => import("./pages/LicensingPage"));
import { captureAttribution } from "./utils/attribution";

// Minimal, unstyled fallback — avoids Layout shift and doesn't pull in extra JS.
const RouteFallback = () => (
  <div
    aria-busy="true"
    style={{
      minHeight: '60vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      color: '#C6A15A',
      fontFamily: 'serif',
      letterSpacing: '0.25em',
    }}
  >
    …
  </div>
);

function App() {
  // Capture UTM + referrer on first load (first-touch attribution model)
  if (typeof window !== 'undefined') captureAttribution();
  return (
    <LanguageProvider>
        <MarketProvider>
          <AuthProvider>
            <SubscriptionProvider>
              <div className="App">
                <BrowserRouter>
                  <ScrollToTop />
                  <AnalyticsProvider>
                    <Suspense fallback={<RouteFallback />}>
                      <Routes>
                      <Route path="/admin" element={<AdminDashboard />} />
                      <Route path="/admin/leads" element={<AdminLeadsPage />} />
                      <Route path="/*" element={
                        <Layout>
                          <Routes>
                            {/* Primary PLEXTERRA nav */}
                            <Route path="/" element={<HomePage />} />
                            <Route path="/about" element={<AboutPage />} />
                            <Route path="/a-propos" element={<Navigate to="/about" replace />} />
                            <Route path="/services" element={<ServicesPage />} />
                            <Route path="/technologies" element={<TechnologiesPage />} />
                            <Route path="/secteurs" element={<SectorsPage />} />
                            <Route path="/sectors" element={<Navigate to="/secteurs" replace />} />
                            <Route path="/projets" element={<ProjectsPage />} />
                            <Route path="/projects" element={<Navigate to="/projets" replace />} />
                            <Route path="/success-stories" element={<SuccessStoriesPage />} />
                            <Route path="/reussites" element={<Navigate to="/success-stories" replace />} />
                            <Route path="/licensing" element={<LicensingPage />} />
                            <Route path="/licences" element={<Navigate to="/licensing" replace />} />
                            <Route path="/regulatory-authorities" element={<Navigate to="/licensing" replace />} />
                            <Route path="/blog" element={<BlogPage />} />
                            <Route path="/blog/:slug" element={<BlogPostPage />} />
                            <Route path="/contact" element={<ContactPage />} />

                            {/* MLI Select calculator */}
                            <Route path="/calculateur-mli-select" element={<MLISelectCalculatorPage />} />
                            <Route path="/mli-select-calculator" element={<Navigate to="/calculateur-mli-select" replace />} />

                            {/* Booking / consultation flows → all redirect to /contact (Feb 2026 policy) */}
                            <Route path="/appel" element={<Navigate to="/contact" replace />} />
                            <Route path="/book-call" element={<Navigate to="/contact" replace />} />

                            {/* Careers */}
                            <Route path="/careers" element={<CareersPage />} />
                            <Route path="/carrieres" element={<Navigate to="/careers" replace />} />

                            {/* Team & internal */}
                            <Route path="/team" element={<TeamPage />} />

                            {/* Removed: Qualification funnel and cart (Feb 2026 - all consultation goes through /contact) */}
                            <Route path="/qualification" element={<Navigate to="/contact" replace />} />
                            <Route path="/checkout" element={<Navigate to="/contact" replace />} />
                            <Route path="/land-analyzer" element={<Navigate to="/contact" replace />} />
                            <Route path="/from-plan-to-permit" element={<Navigate to="/services" replace />} />

                            {/* Preserved calculators & analyses */}
                            <Route path="/analyse-strategique" element={<AnalyseStrategiquePage />} />
                            <Route path="/simulateur-institutionnel" element={<InstitutionalSimulatorPage />} />
                            <Route path="/simulateur" element={<Navigate to="/simulateur-institutionnel" replace />} />
                            <Route path="/roi-calculator" element={<Navigate to="/simulateur-institutionnel" replace />} />

                            {/* Preserved: paid flow, analyses, shared */}
                            <Route path="/land/:landId" element={<SharedLandPage />} />
                            <Route path="/analysis/:id" element={<SharedAnalysisPage />} />
                            <Route path="/payment-success" element={<PaymentSuccessPage />} />
                            <Route path="/my-analyses" element={<MyAnalysesPage />} />
                            <Route path="/pricing" element={<PricingPage />} />
                            <Route path="/quote-request" element={<QuoteRequestPage />} />

                            {/* Redirects for removed multiplex plan routes */}
                            <Route path="/models" element={<Navigate to="/services" replace />} />
                            <Route path="/models/:modelId" element={<Navigate to="/services" replace />} />
                            <Route path="/multifamily-plans" element={<Navigate to="/services" replace />} />
                            <Route path="/icf-multifamily-plans" element={<Navigate to="/services" replace />} />
                            <Route path="/multiplex-plans" element={<Navigate to="/services" replace />} />
                            <Route path="/townhouses" element={<Navigate to="/services" replace />} />
                            <Route path="/plexterra-6" element={<Navigate to="/services" replace />} />
                            <Route path="/plexterra-8" element={<Navigate to="/services" replace />} />
                            <Route path="/plexterra-12" element={<Navigate to="/services" replace />} />
                            <Route path="/plexterra-18" element={<Navigate to="/services" replace />} />
                            <Route path="/plexterra-24" element={<Navigate to="/services" replace />} />
                            <Route path="/products/:slug" element={<Navigate to="/services" replace />} />
                            <Route path="/investors-lenders" element={<Navigate to="/contact" replace />} />

                            {/* Preserved: content resources and SEO pages */}
                            <Route path="/pages/legal-notice" element={<LegalPages type="legal-notice" />} />
                            <Route path="/pages/privacy-policy" element={<LegalPages type="privacy-policy" />} />
                            <Route path="/pages/terms-of-sale" element={<LegalPages type="terms-of-sale" />} />
                            <Route path="/resources" element={<ResourcesPage />} />
                            <Route path="/resources/:slug" element={<ResourceArticlePage />} />
                            <Route path="/contractor-network" element={<ContractorNetworkPage />} />
                            <Route path="/developer-guide" element={<DeveloperGuidePage />} />
                            <Route path="/cities" element={<CitiesIndexPage />} />
                            <Route path="/cities/:slug" element={<CityPage />} />
                            <Route path="/lot-analysis" element={<LotSizesIndexPage />} />
                            <Route path="/lot-analysis/:slug" element={<LotSizePage />} />
                            <Route path="/articles" element={<SEOArticlesPage />} />
                            <Route path="/articles/:slug" element={<SEOArticleDetailPage />} />

                            {/* Legal */}
                            <Route path="/politique-confidentialite" element={<PrivacyPolicyPage />} />
                            <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                            <Route path="/conditions-generales" element={<TermsOfServicePage />} />
                            <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                          </Routes>
                          <ChatWidget />
                        </Layout>
                      } />
                      </Routes>
                    </Suspense>
                    <WhatsAppButton />
                  </AnalyticsProvider>
                </BrowserRouter>
              </div>
            </SubscriptionProvider>
          </AuthProvider>
        </MarketProvider>
      </LanguageProvider>
  );
}

export default App;
